import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Check, ChevronsUpDown, Loader2, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { matchesQuery } from "@/lib/wilayah";

export interface ComboboxOption {
  value: string;
  label: string;
  /** A second, quieter line under the label. Searched too. */
  hint?: string;
}

/**
 * A searchable single-select: a button that opens a filterable list. For the
 * address picker, where a plain select of 500-odd kecamatan is unusable on a
 * phone and free text is how a wrong postal code gets typed.
 *
 * Arrow keys move, Enter picks, Escape closes. A hidden required input lets it
 * take part in the form's native validation like any other field.
 */
export function Combobox({
  id,
  value,
  onChange,
  options,
  placeholder = "Pilih…",
  searchPlaceholder = "Cari…",
  emptyText = "Tidak ditemukan",
  disabled,
  loading,
  required,
}: {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  options: ComboboxOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  loading?: boolean;
  required?: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [active, setActive] = React.useState(0);
  const listRef = React.useRef<HTMLUListElement>(null);
  const listId = React.useId();

  const selected = options.find((o) => o.value === value) ?? null;
  const filtered = React.useMemo(
    () => (query.trim() ? options.filter((o) => matchesQuery(`${o.label} ${o.hint ?? ""}`, query)) : options),
    [options, query],
  );

  React.useEffect(() => setActive(0), [query, open]);
  React.useEffect(() => {
    if (!open) setQuery("");
  }, [open]);
  React.useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active]);

  function pick(v: string) {
    onChange(v);
    setOpen(false);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      // Never submits the checkout form from inside the search box.
      e.preventDefault();
      const o = filtered[active];
      if (o) pick(o.value);
    }
  }

  const isDisabled = disabled || loading;

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <div className="relative">
        <PopoverPrimitive.Trigger asChild>
          <button
            id={id}
            type="button"
            disabled={isDisabled}
            role="combobox"
            aria-expanded={open}
            aria-controls={listId}
            className={cn(
              "flex h-11 w-full items-center justify-between gap-2 rounded-xl border border-input bg-white px-4 text-left text-base transition-colors",
              "focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20",
              "disabled:cursor-not-allowed disabled:opacity-50",
            )}
          >
            <span className={cn("truncate", !selected && "text-muted-foreground")}>
              {loading ? "Memuat…" : (selected?.label ?? placeholder)}
            </span>
            {loading ? (
              <Loader2 className="size-4 shrink-0 animate-spin text-ink/40" />
            ) : (
              <ChevronsUpDown className="size-4 shrink-0 text-ink/40" />
            )}
          </button>
        </PopoverPrimitive.Trigger>
        {required && (
          <input
            tabIndex={-1}
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-0 h-0 w-full opacity-0"
            value={value}
            required
            onChange={() => {}}
          />
        )}
      </div>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={6}
          // Focus goes to the search box, not the first option.
          onOpenAutoFocus={(e) => e.preventDefault()}
          className="z-50 w-[var(--radix-popover-trigger-width)] overflow-hidden rounded-xl border border-input bg-white shadow-lg"
        >
          <div className="flex items-center gap-2 border-b border-input px-3">
            <Search className="size-4 shrink-0 text-ink/40" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={searchPlaceholder}
              aria-controls={listId}
              aria-activedescendant={filtered[active] ? `${listId}-${active}` : undefined}
              className="h-11 w-full bg-transparent text-base placeholder:text-muted-foreground focus:outline-none"
            />
          </div>
          <ul ref={listRef} id={listId} role="listbox" className="max-h-64 overflow-y-auto overscroll-contain py-1">
            {filtered.length === 0 ? (
              <li className="px-4 py-3 text-sm text-ink/55">{emptyText}</li>
            ) : (
              filtered.map((o, i) => (
                <li
                  key={o.value}
                  id={`${listId}-${i}`}
                  data-index={i}
                  role="option"
                  aria-selected={o.value === value}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => pick(o.value)}
                  className={cn(
                    "flex cursor-pointer items-start gap-2 px-3 py-2.5 text-sm",
                    i === active && "bg-primary/8",
                    o.value === value ? "text-primary" : "text-ink",
                  )}
                >
                  <Check className={cn("mt-0.5 size-4 shrink-0", o.value === value ? "opacity-100" : "opacity-0")} />
                  <span className="min-w-0">
                    <span className="block">{o.label}</span>
                    {o.hint && <span className="block text-xs text-ink/50">{o.hint}</span>}
                  </span>
                </li>
              ))
            )}
          </ul>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
